/**
 * DetailPanel Component — Premium Global Restaurant Details
 * Luxury slide-out panel with tabs and community integration
 * v5.0 - International • Community • Premium
 */
import React, { useState, useMemo } from 'react';
import { X, Heart, Navigation, Share2, Award, ExternalLink, Info, MessageSquare, Image, UtensilsCrossed, PenSquare, Store, Soup, Salad, ChefHat, Globe, Coffee, Flame } from 'lucide-react';
import { Badge, Button, Modal } from '../shared';
import QueuePrediction from '../features/queue/QueuePrediction';
import { ReviewList, ReviewForm } from '../features/reviews';
import { CommunityStats } from '../features/community';
import { formatPrice } from '../../data/currencies';
import { SAMPLE_REVIEWS, REVIEW_STATS } from '../../data/reviews';
import { useLocalStorage } from '../../hooks/useLocalStorage';

// Category icon mapping
const CATEGORY_ICONS = {
  'Street Food': <Store size={32} strokeWidth={1.5} />,
  'Pad Thai': <UtensilsCrossed size={32} strokeWidth={1.5} />,
  'Beef Noodle': <Soup size={32} strokeWidth={1.5} />,
  'Noodles': <Soup size={32} strokeWidth={1.5} />,
  'Vegan': <Salad size={32} strokeWidth={1.5} />,
  'Thai': <Flame size={32} strokeWidth={1.5} />,
  'Chinese': <ChefHat size={32} strokeWidth={1.5} />,
  'Indian': <ChefHat size={32} strokeWidth={1.5} />,
  'Fusion': <Globe size={32} strokeWidth={1.5} />,
  'Japanese': <UtensilsCrossed size={32} strokeWidth={1.5} />,
  'International': <Globe size={32} strokeWidth={1.5} />,
  'Cafe': <Coffee size={32} strokeWidth={1.5} />
};

// Tab definitions
const TABS = [
  { id: 'overview', icon: <Info size={16} />, label: { en: 'Overview', th: 'ภาพรวม' } },
  { id: 'reviews', icon: <MessageSquare size={16} />, label: { en: 'Reviews', th: 'รีวิว' } },
  { id: 'photos', icon: <Image size={16} />, label: { en: 'Photos', th: 'รูปภาพ' } },
  { id: 'menu', icon: <UtensilsCrossed size={16} />, label: { en: 'Menu', th: 'เมนู' } },
];

export function DetailPanel({
  restaurant,
  onClose,
  onToggleFavorite,
  onShare,
  isFavorite,
  copied,
  currency,
  t,
  language = 'th',
  onBadgeClick,
  onMenuDecode
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showReviewForm, setShowReviewForm] = useState(false);

  // Get reviews (Sample + User generated)
  const [userReviews, setUserReviews] = useLocalStorage('tastetrace_user_reviews', []);

  const reviews = useMemo(() => {
    if (!restaurant) return [];
    const restaurantUserReviews = userReviews.filter(r => r.restaurantId === restaurant.id);
    return [...restaurantUserReviews, ...(SAMPLE_REVIEWS[restaurant.id] || [])];
  }, [restaurant, userReviews]);

  const reviewStats = useMemo(() => {
    if (!restaurant) return { averageRating: 0, totalReviews: 0, ratingDistribution: {} };

    // Recalculate stats based on merged reviews
    const total = reviews.length;
    if (total === 0) return REVIEW_STATS[restaurant.id] || { averageRating: 0, totalReviews: 0, ratingDistribution: {} };

    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    const avg = sum / total;

    // Simple distribution calc
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => {
      dist[r.rating] = (dist[r.rating] || 0) + 1;
    });

    return {
      averageRating: avg.toFixed(1),
      totalReviews: total,
      ratingDistribution: dist
    };
  }, [reviews, restaurant]);

  if (!restaurant) return null;

  const categoryIcon = CATEGORY_ICONS[restaurant.category] || <UtensilsCrossed size={32} strokeWidth={1.5} />;

  const handleWriteReview = () => {
    setShowReviewForm(true);
  };

  const handleSubmitReview = async (reviewData) => {
    const newReview = {
      ...reviewData,
      id: Date.now(),
      createdAt: new Date().toISOString(),
      userName: 'You', // In a real app, this would come from auth
      helpful: 0,
      images: reviewData.images.map(file => URL.createObjectURL(file)) // Mock image upload
    };

    setUserReviews(prev => [newReview, ...prev]);
    setShowReviewForm(false);
    setActiveTab('reviews');
  };

  return (
    <>
      {/* Panel */}
      <div className={`absolute z-30 flex flex-col bg-white/[0.98] backdrop-blur-2xl transition-transform duration-500 ease-spring
        md:top-4 md:left-4 md:bottom-[5.5rem] md:w-[400px] md:h-auto md:max-h-[calc(100vh-2rem-5.5rem)] md:rounded-[32px] md:shadow-spring md:border md:border-slate-200/50
        bottom-0 left-0 right-0 h-[85vh] rounded-t-[32px] shadow-[0_-8px_40px_rgba(0,0,0,0.12)] border-t border-slate-200/50
        ${restaurant ? 'translate-y-0 md:translate-x-0 opacity-100 pointer-events-auto' : 'translate-y-[120%] md:translate-y-0 md:-translate-x-[120%] opacity-0 pointer-events-none'}
      `}>
        {/* Sticky Header with Back Button */}
        <div className="sticky top-0 z-40 px-5 pt-5 pb-4 bg-white/90 backdrop-blur-xl md:rounded-t-2xl rounded-t-3xl border-b border-neutral-100 shadow-sm flex items-center justify-between">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-neutral-600 hover:text-neutral-900 bg-neutral-50 hover:bg-neutral-100 px-3 py-2 rounded-xl transition-colors font-medium text-[13px]"
          >
            ← {language === 'th' ? 'กลับไปยังผลลัพธ์' : 'Back to results'}
          </button>

          <button
            onClick={onShare}
            className="p-2 text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100 rounded-full transition-colors"
          >
            <Share2 size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-6 pb-36 relative">
          {/* Header */}
          <div className="mt-2 mb-6">
            <div className="flex items-start gap-5">
              {/* Category Icon */}
              <div className="w-20 h-20 bg-slate-50 rounded-[24px] flex items-center justify-center text-4xl flex-shrink-0 shadow-inner relative overflow-hidden group">
                <span className="group-hover:scale-110 transition-transform duration-500 text-neutral-400">{categoryIcon}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
                  className="absolute top-1 right-1 p-1 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:scale-110 transition-all duration-300"
                >
                  <Heart
                    size={14}
                    className={`transition-all ${isFavorite ? 'text-red-500 fill-red-500 animate-heart-fill' : 'text-neutral-300'}`}
                  />
                </button>
              </div>

              <div className="min-w-0 pt-0.5 flex-1 mt-1">
                <h2 className="text-[28px] font-display font-bold text-slate-900 leading-[1.15] tracking-tight">
                  {restaurant.name}
                </h2>
                <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                  <span className="text-overline text-slate-500 uppercase font-bold tracking-wider">{restaurant.category}</span>
                  {restaurant.reviews?.google && (
                    <span className="text-[11px] px-2.5 py-1 bg-gold-50 text-gold-700 rounded-full font-bold">
                      {restaurant.reviews.google} ★
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-2 mt-5">
              {restaurant.badges?.map(b => (
                <Badge
                  key={b}
                  type={b}
                  t={t}
                  onClick={onBadgeClick}
                />
              ))}
              {restaurant.dietary?.map(d => (
                <Badge
                  key={d}
                  type={d}
                  t={t}
                  onClick={onBadgeClick}
                />
              ))}
            </div>
          </div>

          {/* Community Stats */}
          <div className="mb-8">
            <CommunityStats
              restaurant={{
                reviewCount: reviewStats.totalReviews || reviews.length,
                photoCount: restaurant.photos?.length || 0,
                favoriteCount: restaurant.favoriteCount || 0,
                bookmarkCount: restaurant.bookmarkCount || 0
              }}
              language={language}
            />
          </div>

          {/* Tab Navigation */}
          <div className="tab-nav mb-6">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn flex items-center gap-2 ${activeTab === tab.id ? 'active' : ''}`}
              >
                {tab.icon}
                <span>{tab.label[language] || tab.label.en}</span>
                {tab.id === 'reviews' && reviews.length > 0 && (
                  <span className="text-[11px] bg-neutral-200 px-1.5 py-0.5 rounded-full">
                    {reviews.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-8">
                {/* Influencer Hub */}
                {restaurant.influencerReviews?.length > 0 && (
                  <Section title={language === 'th' ? 'เคยอยู่ใน' : 'Featured In'}>
                    <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 scrollbar-hide snap-x">
                      {restaurant.influencerReviews.map((review) => (
                        <a
                          key={review.id}
                          href={review.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-shrink-0 w-52 group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-500 snap-center"
                        >
                          <div className="aspect-video w-full overflow-hidden">
                            <img
                              src={review.thumbnail}
                              alt={review.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            />
                          </div>
                          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent flex items-end">
                            <div className="p-3.5 w-full">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className={`text-[9px] font-bold px-2 py-0.5 rounded text-white ${review.platform === 'youtube' ? 'bg-red-600' : 'bg-black'
                                  }`}>
                                  {review.platform === 'youtube' ? 'YOUTUBE' : 'TIKTOK'}
                                </span>
                                <span className="text-[10px] text-white/75">{review.timestamp}</span>
                              </div>
                              <p className="text-[11px] font-medium text-white line-clamp-2 leading-snug">
                                {review.title}
                              </p>
                            </div>
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <div className="w-12 h-12 bg-white/25 backdrop-blur-md rounded-full flex items-center justify-center border border-white/40">
                              <span className="ml-0.5 text-white text-base">▶</span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </Section>
                )}

                {/* AI Analysis */}
                {restaurant.aiAnalysis && (
                  <div className="grid grid-cols-2 gap-4">
                    {/* Credibility Score */}
                    <div className="bg-neutral-50 p-5 rounded-2xl border border-neutral-100/80 relative overflow-hidden">
                      <div className="absolute top-0 right-0 p-4 opacity-[0.04]">
                        <Award size={60} />
                      </div>
                      <div className="relative z-10">
                        <h4 className="text-overline text-neutral-400 mb-2">
                          {language === 'th' ? 'ความน่าเชื่อถือ' : 'Credibility'}
                        </h4>
                        <div className="flex items-baseline gap-1.5">
                          <span className="text-[36px] font-display font-medium text-neutral-900 leading-none">
                            {restaurant.aiAnalysis.credibilityScore || 80}
                          </span>
                          <span className="text-xs text-neutral-400">/ 100</span>
                        </div>
                        <div className="mt-3 h-1.5 w-full bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-neutral-800 rounded-full transition-all duration-700"
                            style={{ width: `${restaurant.aiAnalysis.credibilityScore || 80}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Quick Stats */}
                <div className="bg-slate-50 p-5 rounded-3xl border border-slate-100/80 flex flex-col justify-center gap-4">
                  {restaurant.reviews?.google && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500 font-bold">Google Maps</span>
                      <span className="font-bold text-slate-800 text-[14px]">{restaurant.reviews.google} <span className="text-gold-500 text-[12px]">★</span></span>
                    </div>
                  )}
                  {restaurant.reviews?.facebook && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500 font-bold">Facebook</span>
                      <span className="font-bold text-slate-800 text-[14px]">{restaurant.reviews.facebook} <span className="text-blue-500 text-[12px]">★</span></span>
                    </div>
                  )}
                  {restaurant.reviews?.wongnai && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500 font-bold">Wongnai</span>
                      <span className="font-bold text-slate-800 text-[14px]">{restaurant.reviews.wongnai} <span className="text-emerald-500 text-[12px]">★</span></span>
                    </div>
                  )}
                </div>

                {/* AI Summary */}
                {restaurant.aiAnalysis?.pros && (
                  <Section title={language === 'th' ? 'สรุปโดย AI' : 'AI Concise Summary'}>
                    <div className="space-y-4">
                      <div className="bg-emerald-50/50 p-5 rounded-2xl border border-emerald-100/60">
                        <div className="flex items-center gap-2.5 mb-4">
                          <div className="w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center text-[10px] text-emerald-700">✓</div>
                          <span className="text-[11px] font-semibold text-emerald-800 uppercase tracking-[0.1em]">
                            {language === 'th' ? 'จุดเด่น' : 'Highlights'}
                          </span>
                        </div>
                        <ul className="space-y-2.5">
                          {restaurant.aiAnalysis.pros.map((p, i) => (
                            <li key={i} className="text-[13px] text-emerald-900/85 pl-3 border-l-2 border-emerald-200 leading-relaxed">
                              {p}
                            </li>
                          ))}
                        </ul>
                      </div>

                      {restaurant.aiAnalysis.cons && (
                        <div className="bg-rose-50/50 p-5 rounded-2xl border border-rose-100/60">
                          <div className="flex items-center gap-2.5 mb-4">
                            <div className="w-5 h-5 bg-rose-100 rounded-full flex items-center justify-center text-[10px] text-rose-700">!</div>
                            <span className="text-[11px] font-semibold text-rose-800 uppercase tracking-[0.1em]">
                              {language === 'th' ? 'ข้อควรพิจารณา' : 'Considerations'}
                            </span>
                          </div>
                          <ul className="space-y-2.5">
                            {restaurant.aiAnalysis.cons.map((p, i) => (
                              <li key={i} className="text-[13px] text-rose-900/85 pl-3 border-l-2 border-rose-200 leading-relaxed">
                                {p}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </Section>
                )}

                {/* Queue Prediction */}
                <QueuePrediction restaurant={restaurant} t={t} />

                {/* Signature Dishes */}
                {restaurant.dishes?.length > 0 && (
                  <Section title={language === 'th' ? 'เมนูแนะนำ' : 'Signature Dishes'}>
                    <div className="grid gap-3">
                      {restaurant.dishes.map((dish, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-neutral-100 hover:border-neutral-200 hover:shadow-md transition-all duration-300 group cursor-pointer"
                        >
                          <div
                            className="w-16 h-16 bg-neutral-100 rounded-xl bg-cover bg-center flex-shrink-0 shadow-soft group-hover:scale-105 transition-transform duration-300"
                            style={{
                              backgroundImage: dish.image ? `url(${dish.image})` : undefined,
                              width: '64px',
                              height: '64px'
                            }}
                          >
                            {!dish.image && (
                              <div className="w-full h-full flex items-center justify-center text-2xl grayscale opacity-40">◈</div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <h4 className="text-[15px] font-display font-bold text-slate-800 tracking-tight pr-3">{dish.name}</h4>
                              <span className="text-[15px] font-semibold text-slate-800 flex-shrink-0">
                                {formatPrice(dish.price, currency)}
                              </span>
                            </div>
                            {dish.isSignature && (
                              <span className="inline-block mt-2 text-[10px] bg-coral-500 text-white px-2.5 py-1 rounded-full font-bold tracking-[0.05em] uppercase shadow-soft">
                                {language === 'th' ? 'ต้องลอง' : 'Must Try'}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Quick Reviews Preview */}
                {reviews.length > 0 && (
                  <div className="pt-4 border-t border-neutral-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-[14px] font-semibold text-neutral-800">
                        {language === 'th' ? 'รีวิวล่าสุด' : 'Recent Reviews'}
                      </h3>
                      <button
                        onClick={() => setActiveTab('reviews')}
                        className="text-[13px] text-brand-500 font-medium hover:text-brand-600"
                      >
                        {language === 'th' ? 'ดูทั้งหมด' : 'See all'} →
                      </button>
                    </div>
                    <div className="space-y-3">
                      {reviews.slice(0, 2).map(review => (
                        <div key={review.id} className="bg-neutral-50 p-4 rounded-xl">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-7 h-7 rounded-full bg-gold-100 flex items-center justify-center text-[11px] font-semibold text-gold-700">
                              {review.userName.charAt(0)}
                            </div>
                            <span className="text-[13px] font-medium text-neutral-800">{review.userName}</span>
                            <span className="text-[12px] text-gold-500">{'★'.repeat(review.rating)}</span>
                          </div>
                          <p className="text-[13px] text-neutral-600 line-clamp-2">{review.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === 'reviews' && (
              <ReviewList
                restaurantId={restaurant.id}
                reviews={reviews}
                language={language}
                onWriteReview={handleWriteReview}
              />
            )}

            {/* Photos Tab */}
            {activeTab === 'photos' && (
              <div className="grid grid-cols-3 gap-2">
                {(restaurant.photos || []).concat(
                  reviews.flatMap(r => r.images || [])
                ).slice(0, 12).map((photo, i) => (
                  <div
                    key={i}
                    className="aspect-square rounded-2xl overflow-hidden bg-slate-100 shadow-soft"
                  >
                    <img
                      src={typeof photo === 'string' ? photo : photo.preview}
                      alt=""
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 ease-spring"
                    />
                  </div>
                ))}
                {(!restaurant.photos || restaurant.photos.length === 0) && reviews.every(r => !r.images?.length) && (
                  <div className="col-span-3 text-center py-12 text-slate-400">
                    {language === 'th' ? 'ยังไม่มีรูปภาพ' : 'No photos yet'}
                  </div>
                )}
              </div>
            )}

            {/* Menu Tab */}
            {activeTab === 'menu' && (
              <div>
                <button
                  onClick={onMenuDecode}
                  className="w-full mb-4 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 p-4 rounded-2xl flex items-center justify-between transition-colors duration-300 border border-emerald-100/50 group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-soft group-hover:scale-105 transition-transform duration-400 ease-spring">
                      <span className="text-xl">💬</span>
                    </div>
                    <div className="text-left">
                      <h4 className="font-bold text-[14px]">{language === 'th' ? 'แปลเมนูด้วย AI' : 'AI Menu Decoder'}</h4>
                      <p className="text-[12px] text-emerald-600/80">{language === 'th' ? 'แปลเมนูและแปลงสกุลเงินทันที' : 'Translate menu and convert prices instantly'}</p>
                    </div>
                  </div>
                  <span className="text-emerald-400 group-hover:translate-x-1 transition-transform">→</span>
                </button>

                {restaurant.dishes?.length > 0 ? (
                  <div className="space-y-3">
                    {restaurant.dishes.map((dish, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-4 p-4 bg-white border border-slate-100 rounded-3xl"
                      >
                        <div
                          className="w-20 h-20 bg-slate-100 rounded-[20px] bg-cover bg-center flex-shrink-0"
                          style={{
                            backgroundImage: dish.image ? `url(${dish.image})` : undefined,
                            width: '80px',
                            height: '80px'
                          }}
                        >
                          {!dish.image && (
                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                              <UtensilsCrossed size={24} />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="text-[16px] font-display font-bold text-slate-800 tracking-tight">{dish.name}</h4>
                              {dish.description && (
                                <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">{dish.description}</p>
                              )}
                            </div>
                            <span className="text-[15px] font-bold text-slate-800">
                              {formatPrice(dish.price, currency)}
                            </span>
                          </div>
                          {dish.isSignature && (
                            <span className="inline-block mt-2 text-[10px] bg-coral-500 text-white px-2.5 py-1 rounded-full font-bold shadow-soft">
                              {language === 'th' ? 'แนะนำ' : 'Recommended'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    {language === 'th' ? 'ยังไม่มีข้อมูลเมนู' : 'Menu not available'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div >
      </div >
      {/* Floating Action Bar */}
      < div className="absolute bottom-4 left-0 w-full px-5 pointer-events-none z-50" >
        <div className="flex gap-2.5 pointer-events-auto shadow-spring rounded-2xl bg-white/70 backdrop-blur-md p-1.5 border border-white/60">
          <a
            href={restaurant.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center bg-coral-500 hover:bg-coral-600 text-white py-3 px-4 rounded-xl font-bold text-[13.5px] shadow-soft transition-all duration-400 ease-spring hover:-translate-y-0.5"
          >
            <Navigation size={16} className="mr-2" /> {language === 'th' ? '?????' : 'Directions'}
          </a>
          <button
            onClick={handleWriteReview}
            className="flex items-center justify-center bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 py-3 px-4 rounded-xl shadow-soft transition-all duration-400 ease-spring font-bold text-[13.5px] hover:-translate-y-0.5"
          >
            <PenSquare size={16} className="mr-2 text-coral-500" /> {language === 'th' ? '???' : 'Review'}
          </button>
        </div>
        {
          copied && (
            <div className="absolute -top-12 left-0 w-full flex justify-center pointer-events-none">
              <div className="bg-slate-800 text-white font-medium text-[12px] px-5 py-2.5 rounded-full shadow-spring animate-fadeIn flex items-center gap-2">
                <span></span> {t('linkCopied')}
              </div>
            </div>
          )
        }
      </div >
      {/* Review Form Modal */}
      < Modal
        isOpen={showReviewForm}
        onClose={() => setShowReviewForm(false)
        }
        title={language === 'th' ? '???????' : 'Write a Review'}
        icon={< PenSquare size={20} />}
      >
        <ReviewForm
          restaurantId={restaurant.id}
          restaurantName={restaurant.name}
          onSubmit={handleSubmitReview}
          onCancel={() => setShowReviewForm(false)}
          language={language}
        />
      </Modal >
    </>
  );
}

/**
 * Section  Refined section header
 */
function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h3 className="text-overline text-slate-400 mb-4 pl-0.5 font-bold tracking-wider">
        {title}
      </h3>
      {children}
    </div>
  );
}

export default DetailPanel;
